/**
 * GenerationActivityLog - Real-time activity log for content generation
 *
 * Shows structured log entries with elapsed time, stuck detection,
 * color-coded severity levels, and CR-2 pipeline stage scores.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, CheckCircle, XCircle, Info, Clock, Activity, Gauge } from 'lucide-react';
import AgentStepTimeline from './AgentStepTimeline';
import type { GenerationLogEntry } from './types';
import { GENERATION_ERROR_MESSAGE } from './generationErrorCopy';

interface GenerationActivityLogProps {
  entries: GenerationLogEntry[];
  startedAt?: string | null;
  isGenerating: boolean;
  status?: string;
  errorMessage?: string | null;
  defaultExpanded?: boolean;
  /** Rendered inside its own page tab — the tab is the disclosure, so the
   *  collapse header becomes a static summary strip and entries always show. */
  embedded?: boolean;
}

const STUCK_WARNING_MS = 60_000; // 60 seconds
const STUCK_DANGER_MS = 120_000; // 120 seconds

const levelConfig: Record<string, { icon: typeof Info; color: string; bg: string }> = {
  info: { icon: Info, color: 'text-muted-foreground', bg: 'bg-muted' },
  warn: { icon: AlertTriangle, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/40' },
  warning: { icon: AlertTriangle, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/40' },
  error: { icon: XCircle, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/40' },
};

function formatElapsed(ms: number): string {
  if (ms < 1000) return '<1s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function formatTimeSince(startedAt: string): string {
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  const elapsed = now - start;
  return formatElapsed(elapsed);
}

/**
 * Partitioned log segment: either a single non-agent entry (rendered with the
 * existing milestone row) or a contiguous run of agent_step entries rendered
 * as a chat-agent-style step timeline.
 */
export type LogSegment =
  | { type: 'entry'; entry: GenerationLogEntry; index: number }
  | { type: 'agent_steps'; steps: GenerationLogEntry[]; key: string };

export function partitionEntries(entries: GenerationLogEntry[]): LogSegment[] {
  const segments: LogSegment[] = [];
  entries.forEach((entry, index) => {
    if (entry.step === 'agent_step') {
      const last = segments[segments.length - 1];
      if (last && last.type === 'agent_steps') {
        last.steps.push(entry);
      } else {
        segments.push({ type: 'agent_steps', steps: [entry], key: `agent-steps-${index}` });
      }
    } else {
      segments.push({ type: 'entry', entry, index });
    }
  });
  return segments;
}

/** Color class for pipeline score badge */
function scoreColor(score: number): string {
  if (score >= 80) return 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400';
  if (score >= 60) return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400';
  return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400';
}

/** Score badge for pipeline stage entries */
const ScoreBadge: React.FC<{ score: number; details?: Record<string, number> }> = ({ score, details }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <span className="relative inline-flex items-center">
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (details && Object.keys(details).length > 0) setShowDetails(!showDetails);
        }}
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${scoreColor(score)} ${details && Object.keys(details).length > 0 ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
        title={details ? Object.entries(details).map(([k, v]) => `${k}: ${v}`).join(', ') : undefined}
      >
        <Gauge className="w-2.5 h-2.5" />
        {score}
      </button>
      {showDetails && details && (
        <div className="absolute left-0 top-full mt-1 z-10 bg-card border border-border rounded shadow-lg px-2 py-1.5 min-w-[140px]">
          {Object.entries(details).map(([key, val]) => (
            <div key={key} className="flex items-center justify-between gap-3 text-[10px] py-0.5">
              <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
              <span className={`font-semibold ${val >= 80 ? 'text-emerald-600 dark:text-emerald-400' : val >= 60 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>{val}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
};

/**
 * `failed`/`retrying` entries historically stored a raw, truncated Python
 * exception repr in `msg` (fixed server-side in
 * backend/template_content_crew/orchestration/customer_safe_errors.py, but
 * rows written before that fix still have it in the DB). That repr can
 * contain per-article LLM cost, token usage, exception class names, and raw
 * HTTP status codes — none of which belongs in this customer-visible tab.
 * Render fixed, honest copy for these two steps instead of `entry.msg`/
 * `entry.output` verbatim, so old rows stop leaking regardless of what the
 * backend already wrote.
 */
const CONTROLLED_STEP_COPY: Partial<Record<string, string>> = {
  failed: GENERATION_ERROR_MESSAGE,
  retrying: 'Generation hit a transient error and is being retried automatically.',
};

/** Customer-safe message for a log entry — bypasses `entry.msg` for steps
 *  whose stored text is not trusted (see CONTROLLED_STEP_COPY above). */
export function displayEntryMessage(entry: GenerationLogEntry): string {
  return CONTROLLED_STEP_COPY[entry.step] ?? entry.msg;
}

/** Whether `entry.output` is safe to render — suppressed for the same
 *  untrusted steps as `displayEntryMessage`. */
export function shouldShowEntryOutput(entry: GenerationLogEntry): boolean {
  return !!entry.output && !(entry.step in CONTROLLED_STEP_COPY);
}

const GenerationActivityLog: React.FC<GenerationActivityLogProps> = ({
  entries,
  startedAt,
  isGenerating,
  status,
  errorMessage,
  defaultExpanded = false,
  embedded = false,
}) => {
  const [expandedState, setExpanded] = useState(defaultExpanded);
  const expanded = embedded || expandedState;
  const [elapsedDisplay, setElapsedDisplay] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Filter out block_complete entries — BlockProgressChecklist handles those.
  // Filter out reentry_verdict entries — an internal per-section QA verdict
  // (the D4 verify-bands gate's re-entry decision), not a customer-facing
  // generation milestone; it stays in generation_log for operator/telemetry
  // reads, it just never renders here.
  const filteredEntries = useMemo(
    () => entries.filter(e => e.step !== 'block_complete' && e.step !== 'reentry_verdict'),
    [entries],
  );

  // Partition into segments: contiguous agent_step runs render as a step
  // timeline; everything else keeps the existing milestone rendering.
  const segments = useMemo(() => partitionEntries(filteredEntries), [filteredEntries]);

  // Compute latest composite score from entries
  const latestScore = useMemo(() => {
    for (let i = entries.length - 1; i >= 0; i--) {
      if (entries[i].step === 'pipeline_composite' && entries[i].score != null) {
        return entries[i].score;
      }
    }
    // Fall back to most recent stage score
    for (let i = entries.length - 1; i >= 0; i--) {
      if (entries[i].score != null) return entries[i].score;
    }
    return null;
  }, [entries]);

  // Update elapsed time every second while generating
  useEffect(() => {
    if (!startedAt || !isGenerating) {
      if (startedAt && entries.length > 0) {
        const lastEntry = entries[entries.length - 1];
        setElapsedDisplay(formatElapsed(lastEntry.elapsed_ms));
      }
      return;
    }

    const update = () => setElapsedDisplay(formatTimeSince(startedAt));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startedAt, isGenerating, entries]);

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (expanded && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries.length, expanded]);

  // Stuck detection
  const stuckState = useMemo(() => {
    if (!isGenerating || entries.length === 0 || !startedAt) return null;

    const lastEntry = entries[entries.length - 1];
    const lastEntryTime = new Date(lastEntry.ts).getTime();
    const now = Date.now();
    const timeSinceLastEntry = now - lastEntryTime;

    if (timeSinceLastEntry >= STUCK_DANGER_MS) {
      return { level: 'danger' as const, message: 'Generation may be stuck - no activity for 2+ minutes' };
    }
    if (timeSinceLastEntry >= STUCK_WARNING_MS) {
      return { level: 'warning' as const, message: 'Progress appears stalled - no activity for 1+ minute' };
    }
    return null;
  }, [isGenerating, entries, startedAt]);

  // Re-check stuck state periodically
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isGenerating) return;
    const interval = setInterval(() => setTick(t => t + 1), 10_000);
    return () => clearInterval(interval);
  }, [isGenerating]);

  if (filteredEntries.length === 0 && !isGenerating) return null;

  const ChevronIcon = expanded ? ChevronDown : ChevronRight;
  const hasScores = entries.some(e => e.score != null);

  const headerBadges = (
    <div className="flex items-center gap-2">
      {latestScore != null && (
        <span className={`inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded font-semibold ${scoreColor(latestScore)}`}>
          <Gauge className="w-3 h-3" />
          {latestScore}
        </span>
      )}
      {stuckState && (
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          stuckState.level === 'danger'
            ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
            : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
        }`}>
          {stuckState.level === 'danger' ? 'Stuck?' : 'Stalled'}
        </span>
      )}
      {elapsedDisplay && (
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          {elapsedDisplay}
        </span>
      )}
      {status === 'completed' && (
        <CheckCircle className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
      )}
      {status === 'failed' && (
        <XCircle className="w-4 h-4 text-red-500 dark:text-red-400" />
      )}
      {isGenerating && !stuckState && (
        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
      )}
    </div>
  );

  return (
    <div className={embedded ? '' : 'border border-border rounded-lg overflow-hidden'}>
      {/* Header: a collapse toggle in the stacked layout; inside the page tab
          (embedded) the tab itself is the disclosure, so this is a static
          summary strip. */}
      {embedded ? (
        <div className="flex items-center justify-between px-3 py-2 bg-muted border-b border-border">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Activity log</span>
            <span className="text-xs text-muted-foreground">({filteredEntries.length} entries)</span>
          </div>
          {headerBadges}
        </div>
      ) : (
        <button
          onClick={() => setExpanded(!expandedState)}
          className="w-full flex items-center justify-between px-3 py-2 bg-muted hover:bg-muted transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <ChevronIcon className="w-4 h-4 text-muted-foreground" />
            <Activity className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Activity Log</span>
            <span className="text-xs text-muted-foreground">({filteredEntries.length} entries)</span>
          </div>
          {headerBadges}
        </button>
      )}

      {/* Entries */}
      {expanded && (
        <div
          ref={scrollRef}
          className={`overflow-y-auto divide-y divide-border ${embedded ? 'max-h-[70vh]' : 'max-h-80'}`}
        >
          {segments.map((segment, segIndex) => {
            if (segment.type === 'agent_steps') {
              // The latest group stays expanded live while generating; once a
              // newer milestone follows (or generation ends) it collapses to
              // a "Completed N steps" header.
              const isLive = isGenerating && segIndex === segments.length - 1;
              return (
                <AgentStepTimeline
                  key={segment.key}
                  steps={segment.steps}
                  isLive={isLive}
                />
              );
            }

            const { entry, index: i } = segment;
            const config = levelConfig[entry.level] || levelConfig.info;
            const Icon = config.icon;
            const isScoreEntry = entry.score != null;
            const bgClass = isScoreEntry ? 'bg-muted/80' : config.bg;

            return (
              <div key={i} className={`px-3 py-1.5 text-xs ${bgClass}`}>
                <div className="flex items-start gap-2">
                  <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${config.color}`} />
                  <span className="text-muted-foreground font-mono w-12 flex-shrink-0 text-right">
                    {formatElapsed(entry.elapsed_ms)}
                  </span>
                  <span className="text-foreground flex-1">{displayEntryMessage(entry)}</span>
                  {isScoreEntry && (
                    <ScoreBadge score={entry.score!} details={entry.score_details} />
                  )}
                </div>
                {/* Output summary (for stage completion entries) */}
                {shouldShowEntryOutput(entry) && (
                  <div className="ml-[68px] mt-0.5 text-[11px] text-muted-foreground leading-tight">
                    {entry.output}
                  </div>
                )}
              </div>
            );
          })}

          {/* Error message. `errorMessage` is `content.error_message`, the
              raw (untruncated) `str(exception)` persisted by the backend —
              same leak class as generation_log msg/output, so it gets the
              same controlled copy rather than rendering verbatim. */}
          {status === 'failed' && errorMessage && (
            <div className="flex items-start gap-2 px-3 py-1.5 text-xs bg-red-50 dark:bg-red-950/40">
              <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400" />
              <span className="text-muted-foreground font-mono w-12 flex-shrink-0 text-right">--</span>
              <span className="text-red-700 dark:text-red-400 flex-1 font-medium">
                {CONTROLLED_STEP_COPY.failed}
              </span>
            </div>
          )}

          {/* Stuck warning */}
          {stuckState && (
            <div className={`flex items-start gap-2 px-3 py-2 text-xs ${
              stuckState.level === 'danger' ? 'bg-red-50 dark:bg-red-950/40' : 'bg-amber-50 dark:bg-amber-950/40'
            }`}>
              <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
                stuckState.level === 'danger' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
              }`} />
              <span className="text-muted-foreground font-mono w-12 flex-shrink-0 text-right">now</span>
              <span className={`flex-1 font-medium ${
                stuckState.level === 'danger' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'
              }`}>
                {stuckState.message}
              </span>
            </div>
          )}

          {/* Pipeline score summary (shown at bottom when scores are available) */}
          {hasScores && !isGenerating && status === 'completed' && (
            <PipelineScoreSummary entries={entries} />
          )}

          {/* Empty state */}
          {filteredEntries.length === 0 && isGenerating && (
            <div className="px-3 py-4 text-center text-xs text-muted-foreground">
              Waiting for activity...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Summary bar showing all pipeline stage scores at the bottom of the log */
const PipelineScoreSummary: React.FC<{ entries: GenerationLogEntry[] }> = ({ entries }) => {
  const stages = useMemo(() => {
    const stageEntries: { step: string; score: number; label: string }[] = [];
    const stageLabels: Record<string, string> = {
      strategy_scored: 'Strategy',
      section_scored: 'Writing',
      assembly_scored: 'Assembly',
      pipeline_composite: 'Overall',
    };

    for (const entry of entries) {
      if (entry.score != null && stageLabels[entry.step]) {
        // For section_scored, aggregate — take the last one (which is the avg)
        const existing = stageEntries.findIndex(s => s.step === entry.step);
        if (existing >= 0) {
          stageEntries[existing].score = entry.score;
        } else {
          stageEntries.push({
            step: entry.step,
            score: entry.score,
            label: stageLabels[entry.step],
          });
        }
      }
    }
    return stageEntries;
  }, [entries]);

  if (stages.length === 0) return null;

  return (
    <div className="px-3 py-2 bg-muted border-t border-border">
      <div className="flex items-center gap-3">
        <Gauge className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Pipeline Scores</span>
        <div className="flex items-center gap-2 flex-1 justify-end">
          {stages.map((stage) => (
            <div key={stage.step} className="flex items-center gap-1">
              <span className="text-[10px] text-muted-foreground">{stage.label}</span>
              <span className={`text-[10px] font-semibold px-1 py-0.5 rounded ${scoreColor(stage.score)}`}>
                {stage.score}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GenerationActivityLog;
